import { AppError } from '@imagise/entities';
import { EditMode, FilterTypes, PictureUnit } from './enums/editor.enum';
import { Rectangle, rotateSize, Size2D } from './interfaces/shared';

export type EditorEditKind =
  | 'crop'
  | 'resize'
  | 'rotate'
  | 'filter'
  | 'watermark'
  | 'deskew'
  | 'animation'
  | 'merge';
export type EditorResizeMode = 'percentage' | 'dimensions';
export type EditorMergeLayout = 'grid' | 'vertical' | 'horizontal' | 'custom';

export interface EditorAnimationFrame {
  id: string;
  name: string;
  sourceBase64: string;
  previewUrl: string;
  mimeType: string | null;
  durationMs: number;
}

export interface EditorAnimationParams {
  width: number;
  height: number;
  backgroundColor: string | null;
  loopCount: number;
  frames: EditorAnimationFrame[];
}

export interface EditorMergeImage {
  id: string;
  name: string;
  sourceBase64: string;
  previewUrl: string;
  mimeType: string | null;
  /** Top-left corner in the merged image; used by the custom layout only. */
  x: number;
  y: number;
}

export interface EditorMergeParams {
  layout: EditorMergeLayout;
  rowCount: number;
  columnCount: number;
  /** Empty gap between images, px. The background shows through it. */
  spacing: number;
  borderWidth: number;
  borderColor: string;
  backgroundColor: string | null;
  images: EditorMergeImage[];
}

export interface EditorSourceImageState {
  fileId: string | null;
  fileName: string;
  sourceBase64: string | null;
  mimeType: string | null;
  previewUrl: string | null;
  width: number | null;
  height: number | null;
}

export interface EditorOutputState {
  toFormat: string | null;
}

export interface EditorHistoryState {
  cursor: number;
}

export interface EditorUiMetaState {
  activeMode: EditMode;
  dirty: boolean;
  loading: boolean;
  error: AppError | null;
}

export interface EditorSobelFilterArgs {
  kind: 'sobel';
  sobelFilterType: number;
}

export interface EditorGaussianBlurFilterArgs {
  kind: 'gaussian-blur';
  stdDeviation: number;
}

export interface EditorBinarizationFilterArgs {
  kind: 'binarization';
  threshold: number;
  lowColor: string;
  highColor: string;
}

export interface EditorExposureFilterArgs {
  kind: 'exposure';
  brightness: number;
  contrast: number;
  gammaRed: number;
  gammaGreen: number;
  gammaBlue: number;
}

export interface EditorBackendFilterArgs {
  kind: 'backend-filter';
  size: number | null;
  sigma: number | null;
  angle: number | null;
}

export type EditorFilterArgs =
  | EditorSobelFilterArgs
  | EditorGaussianBlurFilterArgs
  | EditorBinarizationFilterArgs
  | EditorExposureFilterArgs
  | EditorBackendFilterArgs;

interface EditorEditBase<TKind extends EditorEditKind, TMode extends EditMode> {
  kind: TKind;
  mode: TMode;
}

export interface EditorCropDraftOperation
  extends EditorEditBase<'crop', EditMode.Crop> {
  params: {
    width: number;
    height: number;
    units: PictureUnit;
    rect: Rectangle | null;
  };
}

export interface EditorResizeDraftOperation
  extends EditorEditBase<'resize', EditMode.Resize> {
  params: {
    scaleX: number;
    scaleY: number;
    mode: EditorResizeMode;
    keepAspectRatio: boolean;
  };
}

export interface EditorRotateDraftOperation
  extends EditorEditBase<'rotate', EditMode.Rotate> {
  params: {
    rightAngle: number;
    preciseAngle: number;
    flipX: boolean;
    flipY: boolean;
    cut: boolean;
  };
}

export interface EditorFilterDraftOperation
  extends EditorEditBase<'filter', EditMode.Filter> {
  params: {
    filterType: FilterTypes;
    filterArgs: EditorFilterArgs | null;
  };
}

export interface EditorWatermarkDraftOperation
  extends EditorEditBase<'watermark', EditMode.Watermark> {
  params: {
    text: string;
    textColor: string;
    fontName: string;
    fontSize: number;
    rotationAngleDegrees: number;
  };
}

export interface EditorDeskewDraftOperation
  extends EditorEditBase<'deskew', EditMode.Deskew> {
  params: {
    resizeProportionally: boolean;
    backgroundColor: string | null;
  };
}

export interface EditorAnimationDraftOperation
  extends EditorEditBase<'animation', EditMode.Animation> {
  params: EditorAnimationParams;
}

/**
 * Merge never becomes a committed edit: the backend /edit pipeline cannot
 * express it, so applying the draft replaces the editor source image with the
 * /merge/* result instead.
 */
export interface EditorMergeDraftOperation
  extends EditorEditBase<'merge', EditMode.Merge> {
  params: EditorMergeParams;
}

export type EditorDraftOperation =
  | EditorCropDraftOperation
  | EditorResizeDraftOperation
  | EditorRotateDraftOperation
  | EditorFilterDraftOperation
  | EditorWatermarkDraftOperation
  | EditorDeskewDraftOperation
  | EditorAnimationDraftOperation
  | EditorMergeDraftOperation;

interface EditorCommittedEditBase<
  TKind extends EditorEditKind,
  TMode extends EditMode,
> extends EditorEditBase<TKind, TMode> {
  id: string;
}

export interface EditorCropCommittedEdit
  extends EditorCommittedEditBase<'crop', EditMode.Crop> {
  params: {
    width: number;
    height: number;
    units: PictureUnit;
    rect: Rectangle;
  };
}

export interface EditorResizeCommittedEdit
  extends EditorCommittedEditBase<'resize', EditMode.Resize> {
  params: {
    scaleX: number;
    scaleY: number;
    mode: EditorResizeMode;
    keepAspectRatio: boolean;
  };
}

export interface EditorRotateCommittedEdit
  extends EditorCommittedEditBase<'rotate', EditMode.Rotate> {
  params: {
    rightAngle: number;
    preciseAngle: number;
    flipX: boolean;
    flipY: boolean;
    cut: boolean;
  };
}

export interface EditorFilterCommittedEdit
  extends EditorCommittedEditBase<'filter', EditMode.Filter> {
  params: {
    filterType: FilterTypes;
    filterArgs: EditorFilterArgs | null;
  };
}

export interface EditorWatermarkCommittedEdit
  extends EditorCommittedEditBase<'watermark', EditMode.Watermark> {
  params: {
    text: string;
    textColor: string;
    fontName: string;
    fontSize: number;
    rotationAngleDegrees: number;
  };
}

export interface EditorDeskewCommittedEdit
  extends EditorCommittedEditBase<'deskew', EditMode.Deskew> {
  params: {
    resizeProportionally: boolean;
    backgroundColor: string | null;
  };
}

export interface EditorAnimationCommittedEdit
  extends EditorCommittedEditBase<'animation', EditMode.Animation> {
  params: EditorAnimationParams;
}

export type EditorCommittedEdit =
  | EditorCropCommittedEdit
  | EditorResizeCommittedEdit
  | EditorRotateCommittedEdit
  | EditorFilterCommittedEdit
  | EditorWatermarkCommittedEdit
  | EditorDeskewCommittedEdit
  | EditorAnimationCommittedEdit;

export interface EditorPreparedCommit {
  id: string;
  edit: EditorCommittedEdit;
}

export interface EditorCanvasViewState {
  previewUrl: string | null;
  committedEdits: EditorCommittedEdit[];
  draft: EditorDraftOperation | null;
}

export interface EditorFormValue {
  source: EditorSourceImageState;
  output: EditorOutputState;
  draft: EditorDraftOperation | null;
  committedEdits: EditorCommittedEdit[];
  history: EditorHistoryState;
  ui: EditorUiMetaState;
}

export function createEmptyEditorFormValue(): EditorFormValue {
  return {
    source: {
      fileId: null,
      fileName: '',
      sourceBase64: null,
      mimeType: null,
      previewUrl: null,
      width: null,
      height: null,
    },
    output: {
      toFormat: null,
    },
    draft: null,
    committedEdits: [],
    history: {
      cursor: 0,
    },
    ui: {
      activeMode: EditMode.None,
      dirty: false,
      loading: false,
      error: null,
    },
  };
}

/**
 * Deep-clone helpers for the editor domain state.
 *
 * Every domain value is a plain JSON-like structure, so structuredClone
 * covers all kinds without per-field copying — newly added params fields are
 * cloned automatically instead of being silently dropped.
 */
export function cloneRectangle(rect: Rectangle | null): Rectangle | null {
  return structuredClone(rect);
}

export function cloneFilterArgs(
  filterArgs: EditorFilterArgs | null,
): EditorFilterArgs | null {
  return structuredClone(filterArgs);
}

export function cloneAnimationFrame(frame: EditorAnimationFrame): EditorAnimationFrame {
  return structuredClone(frame);
}

export function cloneAnimationParams(
  params: EditorAnimationParams,
): EditorAnimationParams {
  return structuredClone(params);
}

export function cloneMergeImage(image: EditorMergeImage): EditorMergeImage {
  return structuredClone(image);
}

export function cloneMergeParams(params: EditorMergeParams): EditorMergeParams {
  return structuredClone(params);
}

/** A merge draft is meaningful once there is something to merge. */
export function isMeaningfulMergeDraft(
  draft: EditorDraftOperation | null,
): draft is EditorMergeDraftOperation {
  return draft?.kind === 'merge' && draft.params.images.length > 1;
}

export function cloneDraftOperation(
  draft: EditorDraftOperation | null,
): EditorDraftOperation | null {
  return structuredClone(draft);
}

export function cloneCommittedEdit(edit: EditorCommittedEdit): EditorCommittedEdit {
  return structuredClone(edit);
}

export function cloneCommittedEdits(
  edits: readonly EditorCommittedEdit[],
): EditorCommittedEdit[] {
  return structuredClone(edits) as EditorCommittedEdit[];
}

export function cloneEditorFormValue(value: EditorFormValue): EditorFormValue {
  return structuredClone(value);
}

export function isCropDraftOperation(
  draft: EditorDraftOperation | null,
): draft is EditorCropDraftOperation {
  return draft?.kind === 'crop';
}

export function getAppliedCommittedEdits(
  value: Pick<EditorFormValue, 'committedEdits' | 'history'>,
): EditorCommittedEdit[] {
  return cloneCommittedEdits(value.committedEdits.slice(0, value.history.cursor));
}

export function getEditorCanvasViewState(value: EditorFormValue): EditorCanvasViewState {
  return {
    previewUrl: value.source.previewUrl,
    committedEdits: getAppliedCommittedEdits(value),
    draft: cloneDraftOperation(value.draft),
  };
}

export function applyEditToSize(
  size: Size2D,
  edit: EditorCommittedEdit,
): Size2D {
  switch (edit.kind) {
    case 'crop':
      return {
        width: Math.max(1, Math.round(edit.params.rect.width)),
        height: Math.max(1, Math.round(edit.params.rect.height)),
      };
    case 'resize':
      return {
        width: Math.max(1, Math.round(size.width * edit.params.scaleX)),
        height: Math.max(1, Math.round(size.height * edit.params.scaleY)),
      };
    case 'rotate':
      return getRotatedSize(size, edit.params);
    case 'filter':
      return {
        width: size.width,
        height: size.height,
      };
    case 'watermark':
      return {
        width: size.width,
        height: size.height,
      };
    case 'deskew':
      return {
        width: size.width,
        height: size.height,
      };
    case 'animation':
      return {
        width: Math.max(1, Math.round(edit.params.width || size.width)),
        height: Math.max(1, Math.round(edit.params.height || size.height)),
      };
  }
}

export function getAppliedImageSize(value: EditorFormValue): Size2D | null {
  if (!value.source.width || !value.source.height) {
    return null;
  }

  let size: Size2D = {
    width: value.source.width,
    height: value.source.height,
  };

  for (const edit of value.committedEdits.slice(0, value.history.cursor)) {
    size = applyEditToSize(size, edit);
  }

  return size;
}

function getRotatedSize(
  size: Size2D,
  rotate: EditorRotateCommittedEdit['params'],
): Size2D {
  const rightAngle = ((rotate.rightAngle % 360) + 360) % 360;
  const hasPreciseRotation = rotate.preciseAngle !== 0;

  if ((rotate.cut || !hasPreciseRotation) && (!rightAngle || rightAngle === 180)) {
    return {
      width: Math.max(1, Math.round(size.width)),
      height: Math.max(1, Math.round(size.height)),
    };
  }

  let outputSize: Size2D = {
    width: size.width,
    height: size.height,
  };

  if (!rotate.cut && hasPreciseRotation) {
    outputSize = rotateSize(outputSize, rotate.preciseAngle);
  }

  if (rightAngle && rightAngle !== 180) {
    outputSize = {
      width: outputSize.height,
      height: outputSize.width,
    };
  }

  return {
    width: Math.max(1, Math.round(outputSize.width)),
    height: Math.max(1, Math.round(outputSize.height)),
  };
}
